/**
 * renderModelThumbnail — render a one-shot preview image of a GLB to a PNG data
 * URL. Shared rendering util in web-core (the editor's asset list, the web asset
 * picker, and boilerplate galleries can all reuse it).
 *
 * Loads the model into a throwaway offscreen `Scene` (on the given engine) with
 * its own lights and camera, captures it via a render target, then disposes the
 * scene. It loads a fresh copy from the asset path and shares nothing with the
 * caller's live scene meshes/materials/textures.
 */

import {
  ArcRotateCamera,
  Vector3,
  Color4,
  HemisphericLight,
  DirectionalLight,
  Scene,
  SceneLoader,
  Tools,
  type AbstractMesh,
} from "babylonjs";
import "babylonjs-loaders"; // register the glTF loader.

export async function renderModelThumbnail(
  mainScene: Scene,
  assetPath: string,
  size = 128,
): Promise<string | null> {
  const engine = mainScene.getEngine();

  // Dedicated offscreen scene — disposed wholesale, independent of the caller.
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0, 0, 0, 0); // transparent so it sits on any tile bg

  const hemi = new HemisphericLight("__thumbHemi", new Vector3(0.2, 1, 0.3), scene);
  hemi.intensity = 1.0;
  const dir = new DirectionalLight("__thumbDir", new Vector3(-0.5, -1, -0.7), scene);
  dir.intensity = 1.4;

  let url: string | null = null;
  try {
    const result = await SceneLoader.ImportMeshAsync("", "", assetPath, scene, undefined, ".glb");
    const meshes = (result.meshes as AbstractMesh[]).filter((m) => (m.getTotalVertices?.() ?? 0) > 0);
    if (meshes.length === 0) throw new Error("GLB has no renderable meshes");

    const cam = new ArcRotateCamera("__thumbCam", -Math.PI / 4, Math.PI / 3, 1, Vector3.Zero(), scene);
    cam.minZ = 0.001;
    cam.maxZ = 1_000_000;
    for (const m of meshes) m.computeWorldMatrix(true);
    cam.zoomOn(meshes, true);
    cam.radius *= 1.3; // a little padding around the model
    scene.activeCamera = cam;

    url = await Tools.CreateScreenshotUsingRenderTargetAsync(
      engine,
      cam,
      { width: size, height: size },
      "image/png",
      1, // samples
      true, // antialiasing
    );
  } catch (e) {
    console.warn("web-core: model thumbnail render failed:", e);
  } finally {
    scene.dispose(); // disposes this scene's own meshes/materials/textures only
  }

  return url;
}

export default renderModelThumbnail;
